import { useQueryClient } from "@tanstack/react-query";
import { KeyRound, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const Field = ({ label, value, onChange, testid, auto }) => (
  <label className="field"><span className="field-label">{label}</span>
    <input type="password" className="field-input" value={value} required autoComplete={auto} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
  </label>
);

export const ChangePasswordForm = ({ onDone, temporary }) => {
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (f.next !== f.confirm) {
      setErr("The new passwords don't match");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: f.current, new_password: f.next });
      qc.setQueryData(["me"], data);
      toast.success("Password changed");
      onDone?.();
    } catch (e2) {
      setErr(apiError(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="change-password-form">
      <Field label={temporary ? "Temporary password" : "Current password"} value={f.current} onChange={(current) => setF({ ...f, current })} testid="cp-current" auto="current-password" />
      <Field label="New password" value={f.next} onChange={(next) => setF({ ...f, next })} testid="cp-new" auto="new-password" />
      <Field label="Confirm new password" value={f.confirm} onChange={(confirm) => setF({ ...f, confirm })} testid="cp-confirm" auto="new-password" />
      <p className="text-xs text-ink/50">At least 10 characters, with letters and numbers.</p>
      {err && <p className="text-sm text-red-600" data-testid="cp-error">{err}</p>}
      <button className="btn-primary w-full" disabled={busy} data-testid="cp-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}Save new password
      </button>
    </form>
  );
};

export const ForcePasswordChange = ({ email }) => (
  <div className="grid min-h-screen place-items-center bg-linen px-4 font-sans text-ink" data-testid="force-password-change">
    <div className="w-full max-w-sm rounded-3xl border border-ink/10 bg-white p-8 shadow-xl">
      <p className="eyebrow text-center">Welcome</p>
      <h1 className="mt-1 text-center font-serif text-2xl">Choose your own password</h1>
      <p className="mb-5 mt-2 text-center text-sm text-ink/60">You signed in as {email} with a temporary password. Replace it to continue.</p>
      <ChangePasswordForm temporary />
    </div>
  </div>
);

export const ChangePasswordButton = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="btn-ghost" onClick={() => setOpen(true)} title="Change password" data-testid="change-password-button"><KeyRound className="h-4 w-4" /><span className="hidden sm:inline">Password</span></button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="font-sans sm:max-w-sm" data-testid="change-password-dialog">
          <DialogHeader><DialogTitle>Change password</DialogTitle></DialogHeader>
          <ChangePasswordForm onDone={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
};
